import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CheckCircle, ArrowLeft, ArrowRight, Filter, Clock, Shield, TrendingUp } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';
import WaitlistCTA from '../components/WaitlistCTA';

export default function ForInvestors() {
  const navigate = useNavigate();

  const goToWaitlist = (role) => {
    navigate('/?scroll=waitlist&role=' + role);
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="for-investors-page">
      <PublicNav currentPage="for-investors" />

      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-blue-600/10 text-blue-500 rounded-full text-sm font-medium mb-6">
            FOR INVESTORS
          </div>
          <h1 className="text-4xl font-bold text-[#1e3a5f] mb-6">
            Discover ventures aligned<br />with your investment principles.
          </h1>
          <p className="text-lg text-[#334d6e] max-w-3xl mx-auto">
            Access founders seeking equity-based capital partnerships. Connect with ventures that match your investment thesis and values preferences.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <Card className="bg-white border-slate-200 p-8">
            <Filter className="text-blue-600 mb-4" size={28} />
            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-4">Aligned Venture Discovery</h3>
            <p className="text-[#334d6e] mb-4 text-sm">See founders that match your criteria:</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#4a6382] text-sm">Industries you're interested in</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#4a6382] text-sm">Stages you prefer to engage</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#4a6382] text-sm">Capital sizes aligned with your focus</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#4a6382] text-sm">Geographies you prefer</span>
              </li>
            </ul>
          </Card>

          <Card className="bg-white border-slate-200 p-8">
            <TrendingUp className="text-blue-600 mb-4" size={28} />
            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-4">Values-Based Matching</h3>
            <p className="text-[#334d6e] mb-4 text-sm">Alignment indicators help you prioritize:</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#4a6382] text-sm">How well venture fits your thesis</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#4a6382] text-sm">Mutual interest indicators</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#4a6382] text-sm">Priority-ranked opportunities</span>
              </li>
            </ul>
          </Card>

          <Card className="bg-white border-slate-200 p-8">
            <Shield className="text-blue-600 mb-4" size={28} />
            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-4">Verified Founder Profiles</h3>
            <p className="text-[#334d6e] mb-4 text-sm">Connect with serious founders:</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#4a6382] text-sm">Complete profile information</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#4a6382] text-sm">Team details visible</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#4a6382] text-sm">Traction & progress shared</span>
              </li>
            </ul>
          </Card>

          <Card className="bg-white border-slate-200 p-8">
            <Clock className="text-blue-600 mb-4" size={28} />
            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-4">Efficient Evaluation Process</h3>
            <p className="text-[#334d6e] mb-4 text-sm">Structured conversations:</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#4a6382] text-sm">Direct messaging in platform</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#4a6382] text-sm">NDA-gated pitch deck access</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <span className="text-[#4a6382] text-sm">Context-aware conversations</span>
              </li>
            </ul>
          </Card>
        </div>

        <Card className="bg-white border-slate-200 p-12 text-center">
          <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Ready to Discover Aligned Ventures?</h2>
          <p className="text-lg text-[#334d6e] mb-8">
            Request early access and be first to know when SeedSynch opens for investors.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <WaitlistCTA 
              variant="investor"
              theme="light"
              size="default"
              onClick={() => goToWaitlist('investor')}
            />
            <Link to="/pricing">
              <Button className="btn-secondary text-base px-8 py-5">
                View Pricing
              </Button>
            </Link>
          </div>
        </Card>
      </div>

      <Footer />
    </div>
  );
}
